// eslint-disable-next-line eslint-comments/disable-enable-pair
/* eslint-disable import/prefer-default-export */
import * as Yup from 'yup';
import { setNestedObjectValues } from 'formik';
import { TAR_PROCESS_ENUM } from './constants';

// this function returns {automatedTAR: bool, assistedTAR: bool, automatedTARMessage: string, assistedTARMessage: string}
export function regOptionsSupportedForState(
	oemProfileProps, // must include oemName, oemStatesSupported, websiteUrl
	stateAbbrev,
	selectedStateData,
	uniqueCartVehicleProductIds,
	oemVehicles,
) {
	const { name: oemName, statesSupported: oemStatesSupported, websiteUrl } = oemProfileProps;
	const stateSupported = oemStatesSupported[stateAbbrev];
	if (!stateSupported) {
		const errMsg = `Ekho Dealer does not yet support ${oemName} sales in ${stateAbbrev}. Please select a different state or navigate to ${oemName}'s website for more options.`;
		return {
			regOptionsSupported: [],
			automatedTARMessage: errMsg,
			assistedTARMessage: errMsg,
			automatedTitlingMessage: errMsg,
			assistedTitlingMessage: errMsg,
			noneMessage: errMsg,
		};
	}
	const { tarProcesses } = selectedStateData;
	const {
		automatedTARSupported,
		assistedTARNotSupported,
		automatedTitlingSupported,
		assistedTitlingNotSupported,
		noneSupported,
	} = tarProcesses;
	// For each cartVehicleProductIds, find the vehicle in oemVehicles that matches the productId. The, check that the oemVehicle.vehicleType is contained in the automatedTARSupported array and not in the assistedTARNotSupported array. Do the same for fuelType.

	const regOptionsSupported = [];
	let automatedTARMessage = '';
	let assistedTARMessage = '';
	let automatedTitlingMessage = '';
	let assistedTitlingMessage = '';
	let noneMessage = '';
	const { vehicleTypes: automatedTARVehicleTypes, fuelTypes: automatedTARFuelTypes } =
		automatedTARSupported;
	const {
		vehicleTypes: assistedTARVehicleTypesNotSupported,
		fuelTypes: assistedTARFuelTypesNotSupported,
	} = assistedTARNotSupported;
	const { vehicleTypes: automatedTitlingVehicleTypes, fuelTypes: automatedTitlingFuelTypes } =
		automatedTitlingSupported;
	const {
		vehicleTypes: assistedTitlingVehicleTypesNotSupported,
		fuelTypes: assistedTitlingFuelTypesNotSupported,
	} = assistedTitlingNotSupported;
	const { vehicleTypes: noneVehicleTypesSupported, fuelTypes: noneFuelTypesSupported } =
		noneSupported;
	// eslint-disable-next-line consistent-return
	uniqueCartVehicleProductIds.forEach((productId) => {
		// find the OEM vehicle where oemVehicle.productId === productId

		const oemVehicle = oemVehicles[productId];
		if (!oemVehicle) {
			// Throw an error if the oemVehicle is not found
			const errMsg = `An error has occurred. Please select a different state or navigate to ${oemName}'s website for more options.`;
			return {
				regOptionsSupported: [],
				automatedTARMessage: errMsg,
				assistedTARMessage: errMsg,
				automatedTitlingMessage: errMsg,
				assistedTitlingMessage: errMsg,
				noneMessage: errMsg,
			};
		}
		const { vehicleType, fuelType, registrationConfig } = oemVehicle;
		// First, let's determine automatedTAR
		if (
			automatedTARVehicleTypes.includes(oemVehicle.vehicleType) &&
			automatedTARFuelTypes.includes(oemVehicle.fuelType) &&
			registrationConfig &&
			registrationConfig[TAR_PROCESS_ENUM.AUTOMATED]
		) {
			regOptionsSupported.push(TAR_PROCESS_ENUM.AUTOMATED);
		} else {
			automatedTARMessage = `Ekho does not yet support automated titling and registration for one or more of your selected ${oemName} vehicles in ${selectedStateData.name}. Please select a different state, different registration option, or navigate to ${websiteUrl} for more options.`;
		}
		// Next, let's determine assistedTAR
		if (
			!assistedTARVehicleTypesNotSupported.includes(vehicleType) &&
			!assistedTARFuelTypesNotSupported.includes(fuelType) &&
			registrationConfig &&
			registrationConfig[TAR_PROCESS_ENUM.ASSISTED]
		) {
			regOptionsSupported.push(TAR_PROCESS_ENUM.ASSISTED);
		} else {
			assistedTARMessage = `Ekho does not yet support assisted titling and registration for one or more of your selected ${oemName} vehicles in ${selectedStateData.name}. Please select a different state, different registration option, or navigate to ${websiteUrl} for more options.`;
		}
		// Next, let's determine automatedTitlingSupported
		if (
			automatedTitlingVehicleTypes.includes(oemVehicle.vehicleType) &&
			automatedTitlingFuelTypes.includes(oemVehicle.fuelType) &&
			registrationConfig &&
			registrationConfig[TAR_PROCESS_ENUM.AUTOMATED_TITLING]
		) {
			regOptionsSupported.push(TAR_PROCESS_ENUM.AUTOMATED_TITLING);
		} else {
			automatedTitlingMessage = `Ekho does not yet support automated titling for one or more of your selected ${oemName} vehicles in ${selectedStateData.name}. Please select a different state, different registration option, or navigate to ${websiteUrl} for more options.`;
		}
		// Next, let's determine assistedTitlingNotSupported
		if (
			!assistedTitlingVehicleTypesNotSupported.includes(vehicleType) &&
			!assistedTitlingFuelTypesNotSupported.includes(fuelType) &&
			registrationConfig &&
			registrationConfig[TAR_PROCESS_ENUM.ASSISTED_TITLING]
		) {
			regOptionsSupported.push(TAR_PROCESS_ENUM.ASSISTED_TITLING);
		} else {
			assistedTitlingMessage = `Ekho does not yet support assisted titling for one or more of your selected ${oemName} vehicles in ${selectedStateData.name}. Please select a different state, different registration option, or navigate to ${websiteUrl} for more options.`;
		}
		if (
			noneVehicleTypesSupported.includes(oemVehicle.vehicleType) &&
			noneFuelTypesSupported.includes(oemVehicle.fuelType) &&
			registrationConfig &&
			registrationConfig[TAR_PROCESS_ENUM.NONE]
		) {
			regOptionsSupported.push(TAR_PROCESS_ENUM.NONE);
		} else {
			noneMessage = `Ekho does not yet support opting out of a tilting/registration for one or more of your selected ${oemName} vehicles in ${selectedStateData.name}. Please select a different state, different registration option, or navigate to ${websiteUrl} for more options.`;
		}
	});
	return {
		regOptionsSupported,
		automatedTARMessage,
		assistedTARMessage,
		automatedTitlingMessage,
		assistedTitlingMessage,
		noneMessage,
	};
}

export const getRegistrationStateValidation = ({
	registrationState,
	oemProfileProps,
	supportedStateData,
	cartVehicles,
	oemVehicles,
}) => {
	const errors = {};
	// Initial check for the presence of registrationState
	if (!registrationState) {
		errors.registrationState = 'Registration state is required.';
		return errors;
	}

	if (!supportedStateData) {
		errors.registrationState = 'State configuration is not available.';
		return errors;
	}

	// Further validations if the state is valid
	const uniqueCartVehicleProductIds = [
		...new Set(cartVehicles.map((vehicle) => vehicle.productId)),
	];
	const { regOptionsSupported, assistedTARMessage } = regOptionsSupportedForState(
		oemProfileProps,
		registrationState,
		supportedStateData,
		uniqueCartVehicleProductIds,
		oemVehicles,
	);
	// Checking for available registration options
	if (regOptionsSupported.length === 0) {
		errors.registrationState =
			assistedTARMessage || 'Registration options are not available for this state.';
	}
	return errors;
};

export function getRegistrationAddressValidation(
	oemProfileProps,
	supportedStateData,
	cartVehicles,
	oemVehicles,
	zipExtRequired = false,
) {
	return Yup.object().shape({
		addressLineOne: Yup.string()
			.min(2, 'Too short')
			.max(300, 'Too long')
			.required('Address is required.')
			.matches(
				/^(?!.*\b(?:P\.?\s*O\.?\s*)?BOX\b).*$/i,
				'PO Box addresses are not allowed. Please use a physical address.',
			),
		addressLineTwo: Yup.string()
			.min(2, 'Too short')
			.max(300, 'Too long')
			.matches(
				/^(?!.*\b(?:P\.?\s*O\.?\s*)?BOX\b).*$/i,
				'PO Box addresses are not allowed. Please use a physical address.',
			),
		city: Yup.string().min(2, 'Too short').max(300, 'Too long').required('City is required.'),
		county: Yup.string()
			.min(2, 'Too short')
			.max(300, 'Too long')
			.required('County is required'),
		state: Yup.string()
			.required('State is required.')
			.test('is-valid-state', 'Registration state error.', (value) => {
				const errors = getRegistrationStateValidation({
					registrationState: value,
					oemProfileProps,
					supportedStateData,
					cartVehicles,
					oemVehicles,
				});
				if (Object.keys(errors).length > 0) {
					return this?.createError({ message: errors.registrationState });
				}
				return true;
			}),
		zip: Yup.string()
			.matches(
				zipExtRequired ? /^\d{5}-\d{4}$/ : /^\d{5}(-\d{4})?$/,
				zipExtRequired
					? 'Zip code with 4-digit extension is required.'
					: 'Zip code is required.',
			)
			.required('Zipcode is required.'),
	});
}

// Generalizable func used to check if all the necessary formiks are completed and valid
// We pass this to each infoCollect component since its the same for all of them
export async function formsAreValidFunc(formiks) {
	// Use map to handle async operations and wait for all promises to resolve
	const promises = formiks.map(async (thisFormik) => {
		let theseErrors = {}; // Initialize as empty object to avoid potential undefined issues
		try {
			theseErrors = await thisFormik.validateForm();
		} catch (e) {
			return true; // Assuming an error means the form is not valid
		}

		if (Object.keys(theseErrors).length > 0) {
			thisFormik.setTouched(setNestedObjectValues(theseErrors, true));
			return true; // Return true because there are errors
		}
		return false; // No errors found
	});

	const results = await Promise.all(promises);
	return results.some((x) => x === true); // Return true if any form is not valid
}
